import React, { useCallback, useEffect, useState } from 'react';
import { CircleCheckBig, Link2, RefreshCw } from 'lucide-react';
import * as hr from '../api';
import Loader from '../components/Loader';
import { useHR } from '../HRContext';
import Modal from '../components/Modal';
import { Select } from '../components/Controls';
import { dt } from './biometric/shared';

// Time & Attendance > Biometric Registrations > Unmatched Punches: punches whose device PIN is on no active registration
// (enrolled on the device under a PIN the app did not give, or before the registration was made). Giving the PIN to an
// employee's registration matches every waiting punch at once.

export default function BiometricUnmatched() {
  const { notify } = useHR();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [linking, setLinking] = useState(null);
  const load = useCallback(() => hr.getUnmatchedPunches().then(r => { setD(r); setError(''); }).catch(e => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);

  if (error && !d) return <div className="error">{error}</div>;
  if (!d) return <Loader title="Loading unmatched punches…" subtitle="Grouping punches by device PIN" />;

  return (
    <div className="mdr mds ael bio bdv">
      <div className="mdr-top">
        <div>
          <h2>Unmatched Punches</h2>
          <p>Punches whose device PIN belongs to no employee. Link the PIN to the right employee and all of its punches are matched.</p>
        </div>
        <div className="mdr-top-actions no-print">
          <button className="mdr-btn" onClick={() => load().then(() => notify('Refreshed'))}><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      <div className="mdr-card mdr-table-card">
        <div className="mdr-scroll">
          <table className="mdr-table mds-table ts-table">
            <thead><tr>{d.can.link && <th className="c no-print">Action</th>}<th>Device PIN</th><th className="c">Punches</th><th>First Punch</th><th>Last Punch</th><th>Devices</th></tr></thead>
            <tbody>
              {d.rows.map(r => (
                <tr key={r.pin} className="mdr-row">
                  {d.can.link && <td className="c no-print"><button className="mdr-btn bdv-link" onClick={() => setLinking(r)}><Link2 size={13} /> Link to Employee</button></td>}
                  <td className="mono"><b>{r.pin}</b></td><td className="c">{r.punches}</td>
                  <td className="nowrap">{dt(r.first)}</td><td className="nowrap">{dt(r.last)}</td><td>{r.devices.join(', ')}</td>
                </tr>
              ))}
              {!d.rows.length && <tr><td colSpan={6} className="mdr-empty">Every punch is matched to an employee.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {linking && <LinkModal r={linking} employees={d.employees} notify={notify} onClose={() => setLinking(null)} onDone={() => { setLinking(null); load(); }} />}
    </div>
  );
}

function LinkModal({ r, employees, notify, onClose, onDone }) {
  const [id, setId] = useState('');
  const [busy, setBusy] = useState(false);
  const emp = employees.find(e => String(e.employee_id) === id);
  const go = async () => {
    setBusy(true);
    try { notify((await hr.linkPunchPin({ pin: r.pin, employee_id: id })).message); onDone(); } catch (e) { notify(e.message, 'bad'); setBusy(false); }
  };
  return (
    <Modal title={`Link PIN ${r.pin}`} onClose={onClose}>
      <div className="mdr-form wh-form">
        <p className="muted small">{r.punches} punch(es) from {r.devices.join(', ')} carry PIN <b>{r.pin}</b>. Choose whose they are. Only employees with an active biometric registration are listed.</p>
        <label className="wh-block"><span>Employee</span>
          <Select value={id} onChange={e => setId(e.target.value)}>
            <option value="">Select...</option>
            {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.name} ({e.employee_no}){e.pin ? ` — PIN ${e.pin}` : ' — no PIN'}</option>)}
          </Select></label>
        {emp?.pin && <p className="bdv-warn small">{emp.name} is enrolled under PIN {emp.pin}. Linking replaces it with {r.pin}: re-enrol them on the devices under {r.pin}, or punches under {emp.pin} will stop matching.</p>}
        <div className="mdh-form-foot wh-form-foot">
          <button className="mdr-btn" onClick={onClose}>Cancel</button>
          <button className="mds-run" disabled={!id || busy} onClick={go}><CircleCheckBig size={14} /> {busy ? 'Linking…' : 'Link PIN'}</button>
        </div>
      </div>
    </Modal>
  );
}
